import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Play, Pause, Maximize2, Info, Globe, Users, Lock } from 'lucide-react';
import { apiService } from '../api/apiclient';
import Avatar from './Avatar';


const VisibilityTags = ({ isBrandedContent, allowedOptions = [], value, onChange }) => {
    // 映射表：将 API 返回的字符串映射为 UI 显示内容
    const metaMap = {
        'PUBLIC_TO_EVERYONE': { label: 'Everyone', icon: <Globe size={14} />, canDisable: false },
        'MUTUAL_FOLLOW_FRIENDS': { label: 'Friends', icon: <Users size={14} />, canDisable: false },
        'SELF_ONLY': { label: 'Only you', icon: <Lock size={14} />, canDisable: true },
    };

    // 自动处理商业内容的限制逻辑
    useEffect(() => {
        if (isBrandedContent && value === 'SELF_ONLY') {
            onChange('PUBLIC'); // 如果是商业内容且选了私密，强制切回公开
        }
    }, [isBrandedContent, value, onChange]);

    // 如果 API 还没返回，默认显示 PUBLIC
    const displayOptions = allowedOptions.length > 0 ? allowedOptions : ['PUBLIC'];

    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest text-gray-400">
                    Visibility
                </label>
                {isBrandedContent && (
                    <span className="text-[9px] text-red-500 font-bold animate-pulse">
                        Public/Friends required for Branded Content
                    </span>
                )}
            </div>

            <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${displayOptions.length}, minmax(0, 1fr))` }}>
                {displayOptions.map((optId) => {
                    const config = metaMap[optId] || { label: optId, icon: <Globe size={14} /> };
                    const isDisabled = config.canDisable && isBrandedContent;
                    const isSelected = value === optId;

                    return (
                        <button
                            key={optId}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => onChange(optId)}
                            className={`
                                flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl border-2 transition-all
                                ${isDisabled
                                    ? 'bg-gray-100 border-transparent opacity-40 cursor-not-allowed text-gray-400'
                                    : isSelected
                                        ? 'bg-black border-black text-white shadow-md scale-[1.02]'
                                        : 'bg-gray-50 border-gray-100 text-gray-500 hover:border-gray-300'
                                }
                            `}
                        >
                            {config.icon}
                            <span className="text-[10px] font-bold tracking-tight">{config.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const TikTokPostPreview = ({ account, video, content, settings, onSettingsChange }) => {
    // 1. 🎯 初始化状态，自上而下注入
    const [disclosure, setDisclosure] = useState(settings?.disclosure || false);
    const [brandSettings, setBrandSettings] = useState({
        yourBrand: settings?.is_your_brand ?? true,
        brandedContent: settings?.is_branded_content ?? false
    });
    const [visibility, setVisibility] = useState(settings?.privacy_level || 'PUBLIC');
    const [allowSettings, setAllowSettings] = useState({
        comment: settings?.allow_comment ?? true,
        duet: settings?.allow_duet ?? true,
        stitch: settings?.allow_stitch ?? true
    });

    const videoRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [loading, setLoading] = useState(true);
    const [creatorData, setCreatorData] = useState(null);

    // 2. 🚀 核心数据流枢纽：替代老旧的全局 useEffect 监听
    const notifyParent = (updatedFields) => {
        if (!onSettingsChange) return;

        // 合并最新变化并立刻向上安全冒泡
        onSettingsChange({
            privacy_level: updatedFields.privacy_level ?? visibility,
            allow_comment: updatedFields.allow_comment ?? allowSettings.comment,
            allow_duet: updatedFields.allow_duet ?? allowSettings.duet,
            allow_stitch: updatedFields.allow_stitch ?? allowSettings.stitch,
            content_disclosure: updatedFields.disclosure ?? disclosure,
            is_your_brand: updatedFields.is_your_brand ?? brandSettings.yourBrand,
            is_branded_content: updatedFields.is_branded_content ?? brandSettings.brandedContent
        });
    };

    // 3. 🛡️ 稳固的 TikTok 创作者信息拉取管道
    useEffect(() => {
        const fetchCreator = async () => {
            if (!account?.id) return;
            try {
                setLoading(true);
                const res = await apiService.getTikTokCreatorInfo(account.id);
                if (res.creatorData?.data) {
                    const data = res.creatorData.data;
                    setCreatorData(data);

                    // 如果父组件没有提供初始隐私级别，则使用官方首选项
                    if (!settings?.privacy_level && data.privacy_level_options?.length > 0) {
                        const defaultPrivacy = data.privacy_level_options[0];
                        setVisibility(defaultPrivacy);
                        notifyParent({ privacy_level: defaultPrivacy });
                    }
                }
            } catch (e) {
                console.error("Failed to load creator info", e);
            } finally {
                setLoading(false);
            }
        };
        fetchCreator();
    }, [account?.id]); // 💡 焊死依赖为基本类型 ID，斩断对象引用带来的重复请求

    const togglePlay = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    };

    const videoData = useMemo(() => {
        const defaultMeta = { url: "", filename: "Unknown", format: "MP4", size: "Unknown", resolution: "1080P", duration: "00:00" };

        if (!video) return defaultMeta;
        if (typeof video === 'object' && video.preview) {
            const isFile = video.file instanceof File;
            return {
                url: video.preview,
                filename: isFile ? video.file.name : (video.id || "Video"),
                format: isFile ? video.file.type.split('/')[1]?.toUpperCase() : "MP4",
                size: isFile ? (video.file.size / (1024 * 1024)).toFixed(2) + "MB" : "---",
                resolution: "1080P",
                duration: video.duration ? `00:${Math.round(video.duration).toString().padStart(2, '0')}` : "00:00"
            };
        }

        if (typeof video === 'string') {
            return { ...defaultMeta, url: video, filename: video.split('/').pop().split('?')[0] || "Remote" };
        }

        if (video instanceof File) {
            return {
                url: URL.createObjectURL(video),
                filename: video.name,
                format: video.type.split('/')[1]?.toUpperCase() || "MOV",
                size: (video.size / (1024 * 1024)).toFixed(2) + "MB",
                resolution: "Auto",
                duration: "00:00"
            };
        }
        return defaultMeta;
    }, [video]);

    const getDeclaration = () => {
        const linkStyle = "font-bold text-black hover:underline mx-1";
        const MusicLink = <a href="https://www.tiktok.com/legal/music-usage-confirmation" target="_blank" rel="noreferrer" className={linkStyle}>Music Usage Confirmation</a>;
        const CommunityLink = <a href="https://www.tiktok.com/community-guidelines" target="_blank" rel="noreferrer" className={linkStyle}>Community Guidelines</a>;
        const BrandedLink = <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer" className={linkStyle}>Branded Content Policy</a>;
        if (!disclosure) {
            return <span>By posting, you agree to our {MusicLink} and TikTok's {CommunityLink}.</span>;
        }
        if (brandSettings.brandedContent) {
            return <span>By posting, you agree to TikTok's {BrandedLink} and {MusicLink}.</span>;
        }
        return <span>By posting, you agree to TikTok's {MusicLink}.</span>;
    };

    const formatTime = (s) => {
        const mins = Math.floor(s / 60) || 0;
        const secs = Math.floor(s % 60) || 0;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="flex flex-col lg:flex-row bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 max-w-5xl mx-auto font-sans mb-6 items-stretch">
            {/* 左侧：视频播放墙与多媒体数据卡 */}
            <div className="w-full lg:w-[42%] bg-black p-5 flex flex-col justify-start">
                <div className="flex justify-between items-center text-white/70 mb-3">
                    <div className="flex items-center gap-2 italic text-xs font-bold">
                        <span className="text-green-400 animate-pulse">●</span> Your video is ready!
                    </div>
                    <Maximize2 size={16} className="cursor-pointer hover:text-white" />
                </div>

                <div onClick={togglePlay} className="relative bg-[#121212] rounded-xl flex items-center justify-center overflow-hidden border border-white/10 shadow-inner aspect-[9/14] max-h-[500px] w-full mx-auto cursor-pointer">
                    {videoData.url ? (
                        <video
                            ref={videoRef}
                            src={videoData.url}
                            className="h-full w-full object-contain"
                            controls={false}
                            autoPlay
                            muted
                            loop
                            onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                        />
                    ) : (
                        <div className="text-gray-700 flex flex-col items-center gap-2">
                            <Play size={20} fill="currentColor" />
                            <span className="text-[10px]">No video source</span>
                        </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                        <div className="flex items-center gap-2 text-white text-[9px]">
                            {isPlaying ? <Pause size={10} fill="white" /> : <Play size={10} fill="white" />}
                            <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
                            <div className="flex-1 h-0.5 bg-white/20 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-white rounded-full transition-all duration-100"
                                    style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-1 mt-4 border-t border-white/10 pt-4 text-center">
                    {['Filename', 'Format', 'Resolution', 'Size'].map((label, idx) => (
                        <div key={label}>
                            <p className="text-[9px] text-gray-500 uppercase font-bold tracking-tighter">{label}</p>
                            <p className="text-[10px] text-white truncate px-1">
                                {Object.values(videoData)[idx + 1]}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* 右侧：发帖业务配置面板 */}
            <div className="w-full lg:w-[58%] p-6 space-y-5 flex flex-col bg-white border-l border-gray-50">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        {loading ? (
                            <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse" />
                        ) : (
                            <Avatar src={creatorData?.creator_avatar_url || account?.avatar} name={creatorData?.creator_nickname || account?.account_name} className="w-9 h-9 border border-white" alt="" />
                        )}
                        <div className="absolute -bottom-0.5 -right-0.5 bg-black text-white rounded-full p-0.5 scale-75 border border-white">
                            <svg viewBox="0 0 24 24" width="8" height="8" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <span className="font-bold text-sm text-gray-900 leading-tight">
                            {loading ? "Loading..." : (creatorData?.creator_nickname || account?.account_name || "Account")}
                        </span>
                        <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-tight">
                            @{creatorData?.creator_username || "tiktok_user"}
                        </span>
                    </div>
                </div>

                <div className="space-y-1">
                    <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Caption</label>
                        <span className="text-[9px] text-gray-400">{content?.length || 0}/2200</span>
                    </div>
                    <div className="w-full p-3 bg-gray-50 rounded-xl text-xs text-gray-600 border border-gray-100 leading-snug min-h-[60px]">
                        {content || <span className="text-gray-300 italic">No description...</span>}
                    </div>
                </div>

                <div className="space-y-4">
                    {/* 🔧 修复点 1: 绑定切换回调，同步最新隐私级别 */}
                    <VisibilityTags
                        isBrandedContent={brandSettings.brandedContent}
                        allowedOptions={creatorData?.privacy_level_options}
                        value={visibility}
                        onChange={(nextVisibility) => {
                            setVisibility(nextVisibility);
                            notifyParent({ privacy_level: nextVisibility });
                        }}
                    />
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Allow:</label>
                        <div className="flex gap-4">
                            {['comment', 'duet', 'stitch'].map((key) => (
                                <label key={key} className="flex items-center gap-1.5 cursor-pointer group">
                                    {/* 🔧 修复点 2: 全面拦截 Checkbox 交互，组合最新原子状态并上报 */}
                                    <input
                                        type="checkbox"
                                        checked={allowSettings[key]}
                                        onChange={() => {
                                            const nextAllow = { ...allowSettings, [key]: !allowSettings[key] };
                                            setAllowSettings(nextAllow);
                                            notifyParent({
                                                allow_comment: nextAllow.comment,
                                                allow_duet: nextAllow.duet,
                                                allow_stitch: nextAllow.stitch
                                            });
                                        }}
                                        className="h-3.5 w-3.5 accent-[#fe2c55] cursor-pointer shadow-sm"
                                    />
                                    <span className="text-[11px] font-bold text-gray-500 group-hover:text-black transition-colors capitalize">
                                        {key}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-gray-800 uppercase tracking-tighter">Disclose content</span>
                            <Info size={12} className="text-gray-300 hover:text-gray-600 cursor-help" />
                        </div>
                        {/* 🔧 修复点 3: 绑定商业披露主开关回调 */}
                        <button
                            onClick={() => {
                                const nextDisclosure = !disclosure;
                                setDisclosure(nextDisclosure);
                                notifyParent({ disclosure: nextDisclosure });
                            }}
                            className={`w-9 h-5 rounded-full transition-all duration-300 relative ${disclosure ? 'bg-[#25F4EE]' : 'bg-gray-200'}`}
                        >
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all duration-300 ${disclosure ? 'left-4.5' : 'left-0.5'}`} />
                        </button>
                    </div>
                    <p className="text-xs">Turn on disclose that this video promotes goods or services in exchange for something of value.</p>

                    {disclosure && (
                        <div className="space-y-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                            {/* 🔧 修复点 4: 绑定 Your brand 子开关 */}
                            <div className={`p-3 rounded-xl border-2 transition-all duration-200 ${brandSettings.yourBrand ? 'bg-white border-[#25F4EE] shadow-sm' : 'bg-transparent border-transparent opacity-60'}`}>
                                <label className="flex items-center justify-between cursor-pointer">
                                    <span className={`text-[11px] font-bold ${brandSettings.yourBrand ? 'text-black' : 'text-gray-500'}`}>
                                        Your brand
                                    </span>
                                    <input
                                        type="checkbox"
                                        checked={brandSettings.yourBrand}
                                        onChange={() => {
                                            const nextBrand = !brandSettings.yourBrand;
                                            setBrandSettings(p => ({ ...p, yourBrand: nextBrand }));
                                            notifyParent({ is_your_brand: nextBrand });
                                        }}
                                        className="accent-[#25F4EE] h-4 w-4"
                                    />
                                </label>
                                <p className="text-[10px] text-gray-400 mt-1">You are promoting yourself or your own business. This video will be classified as Brand Organic.</p>
                            </div>

                            {/* 🔧 修复点 5: 绑定 Branded content 子开关 */}
                            <div className={`p-3 rounded-xl border-2 transition-all duration-200 ${brandSettings.brandedContent ? 'bg-white border-[#25F4EE] shadow-sm' : 'bg-transparent border-transparent opacity-60'}`}>
                                <label className="flex items-center justify-between cursor-pointer">
                                    <span className={`text-[11px] font-bold ${brandSettings.brandedContent ? 'text-black' : 'text-gray-500'}`}>
                                        Branded content
                                    </span>
                                    <input
                                        type="checkbox"
                                        checked={brandSettings.brandedContent}
                                        onChange={() => {
                                            const nextBranded = !brandSettings.brandedContent;
                                            setBrandSettings(p => ({ ...p, brandedContent: nextBranded }));
                                            notifyParent({ is_branded_content: nextBranded });
                                        }}
                                        className="accent-[#25F4EE] h-4 w-4"
                                    />
                                </label>
                                <p className="text-[10px] text-gray-400 mt-1">You are promoting another brand or a third party. This video will be classified as Branded Content.</p>
                            </div>
                        </div>
                    )}
                </div>
                <div className="mt-auto pt-2 space-y-4">
                    <div className="p-2 bg-gray-50 rounded-lg min-h-[40px] flex items-center justify-center">
                        <p className="text-[9px] text-gray-400 text-center leading-tight font-medium italic">
                            {getDeclaration()}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TikTokPostPreview;
